'use client';
/**
 * The marketing page's actual content and interactivity.
 *
 * Split out of app/page.tsx so that file can be a plain server component:
 * a route needs to export `metadata` to set its own <title> distinct from
 * the root layout's, and a 'use client' file cannot export `metadata` at
 * all. This is the client half of that split; page.tsx is the thin server
 * half that supplies the title and renders this.
 */
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useApp } from '@/lib/store';

/** Anchor-scroll targets for the marketing nav. */
const SECTIONS = [
  { label: 'Features', id: 'features' },
  { label: 'How it works', id: 'how-it-works' },
  { label: 'Pricing', id: 'pricing' },
];

const FEATURES = [
  {
    id: 'feat-ai-architect',
    icon: 'CpuChipIcon',
    title: 'AI Architect',
    desc: 'Guided chat that asks about scale, budget, and compliance — then proposes a right-sized architecture.',
    color: 'text-purple-400',
    bg: 'bg-purple-900/20 border-purple-800/40',
  },
  {
    id: 'feat-generate',
    icon: 'SparklesIcon',
    title: 'Generate Diagram',
    desc: 'Describe your stack in plain English. Get a schema-validated diagram with all services connected.',
    color: 'text-blue-400',
    bg: 'bg-blue-900/20 border-blue-800/40',
  },
  {
    id: 'feat-cost',
    icon: 'CurrencyDollarIcon',
    title: 'Cost Estimate',
    desc: 'Per-resource monthly cost breakdown with savings tips. Recalculates when you change regions or services.',
    color: 'text-green-400',
    bg: 'bg-green-900/20 border-green-800/40',
  },
  {
    id: 'feat-terraform',
    icon: 'CodeBracketIcon',
    title: 'Terraform Export',
    desc: 'One-click HCL export. Customize names, region, and modules — or download the validated default.',
    color: 'text-orange-400',
    bg: 'bg-orange-900/20 border-orange-800/40',
  },
  {
    id: 'feat-import',
    icon: 'ArrowUpTrayIcon',
    title: 'Import Terraform',
    desc: 'Reverse-engineer existing .tf files into a live diagram. Traced to file and line number.',
    color: 'text-cyan-400',
    bg: 'bg-cyan-900/20 border-cyan-800/40',
  },
  {
    id: 'feat-repo',
    icon: 'CodeBracketSquareIcon',
    title: 'Repo Sync',
    desc: 'Push diagrams, specs, and Terraform to GitHub or Azure Repos. Deploy straight from the app.',
    color: 'text-pink-400',
    bg: 'bg-pink-900/20 border-pink-800/40',
  },
];

const STEPS = [
  {
    id: 'step-1',
    num: '01',
    title: 'Describe',
    desc: "Tell Archlyy what you're building — the workload, traffic expectations, and budget. Use guided chat or a free-form prompt.",
  },
  {
    id: 'step-2',
    num: '02',
    title: 'Generate',
    desc: 'Archlyy validates the architecture, draws the diagram, calculates the monthly cost, and writes the Terraform — all at once.',
  },
  {
    id: 'step-3',
    num: '03',
    title: 'Deploy',
    desc: 'Export the Terraform ZIP, push to your repo, or copy the HCL directly. Your architecture is ready to deploy.',
  },
];

const CLOUDS = [
  {
    id: 'cloud-aws',
    name: 'AWS',
    color: 'text-aws',
    border: 'border-aws/30',
    bg: 'bg-amber-900/20',
  },
  {
    id: 'cloud-azure',
    name: 'Azure',
    color: 'text-azure',
    border: 'border-azure/30',
    bg: 'bg-blue-900/20',
  },
  {
    id: 'cloud-gcp',
    name: 'GCP',
    color: 'text-gcp',
    border: 'border-gcp/30',
    bg: 'bg-blue-900/20',
  },
];

const TESTIMONIALS = [
  {
    id: 'test-1',
    name: 'Priya Nair',
    role: 'Senior Cloud Architect, Infosys',
    text: 'Cut our architecture review cycle from 2 days to 20 minutes. The Terraform output is production-ready — not a scaffold.',
    avatar: 'PN',
  },
  {
    id: 'test-2',
    name: 'Marcus Chen',
    role: 'DevOps Lead, Grab',
    text: 'The cost estimate alone saved us from over-provisioning a data pipeline by $2,400/month. The Well-Architected insights are genuinely useful.',
    avatar: 'MC',
  },
  {
    id: 'test-3',
    name: 'Aisha Okonkwo',
    role: 'Solutions Engineer, Flutterwave',
    text: 'I imported our legacy Terraform and got a diagram in 30 seconds. Finally understood what we were actually running.',
    avatar: 'AO',
  },
];

const PRICING = [
  {
    id: 'plan-free',
    name: 'Free',
    price: '$0',
    period: '/mo',
    desc: 'For individuals exploring cloud design.',
    features: ['5 diagrams', 'AWS only', 'Cost estimate', 'PNG export', 'Community support'],
    cta: 'Start Free',
    highlight: false,
  },
  {
    id: 'plan-pro',
    name: 'Pro',
    price: '$29',
    period: '/mo',
    desc: 'For engineers who design infrastructure daily.',
    features: [
      'Unlimited diagrams',
      'AWS + Azure + GCP',
      'Terraform export',
      'Repo sync (1 repo)',
      'Import Terraform',
      'Version history',
      'Priority support',
    ],
    cta: 'Start Pro Trial',
    highlight: true,
  },
  {
    id: 'plan-team',
    name: 'Team',
    price: '$89',
    period: '/mo',
    desc: 'For architecture teams with shared workflows.',
    features: [
      'Everything in Pro',
      'Up to 10 seats',
      'Shared blueprints',
      'Unlimited repo sync',
      'Admin panel',
      'SSO / SAML',
      'Dedicated support',
    ],
    cta: 'Contact Sales',
    highlight: false,
  },
];

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, loading } = useApp();

  // A signed-in visitor should land in the app, not be asked to sign up again.
  const primaryHref = user ? '/dashboard' : '/login';
  const primaryLabel = user ? 'Open Archlyy' : 'Start Free';

  return (
    <div
      className="min-h-screen theme-dark-pin"
      style={{ background: 'linear-gradient(180deg, #020817 0%, #0a0f1e 30%, #0a0f1e 100%)' }}
    >
      {/* Top Nav */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 border-b border-border/50"
        style={{ background: 'rgba(10, 15, 30, 0.9)', backdropFilter: 'blur(12px)' }}
      >
        <div className="max-w-screen-2xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-semibold text-lg tracking-tight">Archlyy</span>
            <span className="text-2xs font-bold px-1.5 py-0.5 rounded border border-primary/40 text-primary ml-1">
              v1.0
            </span>
          </div>
          <div className="hidden md:flex items-center gap-1">
            {SECTIONS.map((item) => (
              <a key={`nav-${item.id}`} href={`#${item.id}`} className="btn-ghost text-sm">
                {item.label}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-2">
            {!loading && !user && (
              <Link href="/login" className="btn-ghost hidden md:block">
                Sign in
              </Link>
            )}
            <Link href={primaryHref} className="btn-primary">
              {primaryLabel}
            </Link>
            <button
              className="md:hidden btn-ghost p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
              aria-expanded={mobileMenuOpen}
            >
              <Icon name={mobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={20} />
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border/50 px-6 py-2 flex flex-col animate-fade-in">
            {SECTIONS.map((item) => (
              <a
                key={`m-nav-${item.id}`}
                href={`#${item.id}`}
                onClick={() => setMobileMenuOpen(false)}
                className="btn-ghost text-sm text-left py-2"
              >
                {item.label}
              </a>
            ))}
            {!user && (
              <Link
                href="/login"
                className="btn-ghost text-sm text-left py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Sign in
              </Link>
            )}
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-24 px-6 max-w-screen-2xl mx-auto">
        <div className="text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary text-xs font-medium mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse-slow inline-block"></span>
            Now supporting AWS, Azure, and GCP
          </div>
          <h1 className="text-5xl md:text-6xl font-bold leading-tight mb-6">
            Design Cloud Infrastructure
            <br />
            <span className="text-gradient-multi">in Minutes, Not Days</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
            Describe your architecture in plain English. Archlyy generates a validated diagram,
            monthly cost estimate, and production-ready Terraform — across AWS, Azure, and GCP.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              href={primaryHref}
              className="btn-primary px-6 py-3 text-base font-semibold glow-blue-sm"
            >
              {user ? 'Open Archlyy' : 'Start Building Free'}
            </Link>
            <Link
              href={user ? '/ai-architect' : '/login'}
              className="btn-secondary px-6 py-3 text-base font-semibold flex items-center gap-2"
            >
              <Icon name="CpuChipIcon" size={16} />
              Talk to the Architect
            </Link>
          </div>
          <div className="flex items-center justify-center gap-6 mt-8">
            {CLOUDS.map((c) => (
              <span
                key={c.id}
                className={`text-xs font-semibold px-3 py-1 rounded-full border ${c.border} ${c.bg} ${c.color}`}
              >
                {c.name}
              </span>
            ))}
          </div>
        </div>

        {/* Hero preview */}
        <div className="mt-16 max-w-5xl mx-auto rounded-xl border border-border overflow-hidden node-shadow">
          <div className="h-10 bg-card border-b border-border flex items-center px-4 gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span className="text-xs text-muted-foreground ml-3 font-mono">
              archlyy.in/editor — AWS E-Commerce Platform
            </span>
          </div>
          <div className="relative overflow-hidden" style={{ background: '#000', minHeight: 340 }}>
            {/* SVG connections */}
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              style={{ overflow: 'visible' }}
            >
              <defs>
                <marker
                  id="hero-arrow"
                  markerWidth="6"
                  markerHeight="6"
                  refX="3"
                  refY="3"
                  orient="auto"
                >
                  <path d="M0,0 L0,6 L6,3 z" fill="rgba(59,130,246,0.5)" />
                </marker>
              </defs>
              {/* CDN → ALB */}
              <line
                x1="50%"
                y1="88"
                x2="50%"
                y2="130"
                stroke="rgba(59,130,246,0.45)"
                strokeWidth="1.5"
                markerEnd="url(#hero-arrow)"
              />
              {/* ALB → EC2 ASG */}
              <line
                x1="50%"
                y1="182"
                x2="32%"
                y2="224"
                stroke="rgba(59,130,246,0.45)"
                strokeWidth="1.5"
                markerEnd="url(#hero-arrow)"
              />
              {/* ALB → RDS */}
              <line
                x1="50%"
                y1="182"
                x2="68%"
                y2="224"
                stroke="rgba(59,130,246,0.45)"
                strokeWidth="1.5"
                markerEnd="url(#hero-arrow)"
              />
              {/* EC2 → S3 */}
              <line
                x1="32%"
                y1="278"
                x2="18%"
                y2="278"
                stroke="rgba(59,130,246,0.3)"
                strokeWidth="1.5"
                markerEnd="url(#hero-arrow)"
              />
              {/* EC2 → ElastiCache */}
              <line
                x1="32%"
                y1="278"
                x2="50%"
                y2="278"
                stroke="rgba(59,130,246,0.3)"
                strokeWidth="1.5"
                markerEnd="url(#hero-arrow)"
              />
              {/* RDS → SQS */}
              <line
                x1="68%"
                y1="278"
                x2="82%"
                y2="278"
                stroke="rgba(59,130,246,0.3)"
                strokeWidth="1.5"
                markerEnd="url(#hero-arrow)"
              />
            </svg>

            {/* Row 1: CloudFront */}
            <div className="flex justify-center pt-8">
              <div
                className="rounded-lg px-4 py-2.5 text-center"
                style={{
                  border: '1.5px solid rgba(245,158,11,0.7)',
                  background: 'rgba(0,0,0,0.8)',
                  minWidth: 120,
                }}
              >
                <div className="text-xs font-mono mb-1" style={{ color: 'rgba(245,158,11,0.6)' }}>
                  CDN
                </div>
                <div className="text-sm font-bold font-mono" style={{ color: '#f59e0b' }}>
                  CloudFront
                </div>
              </div>
            </div>

            {/* Row 2: ALB */}
            <div className="flex justify-center mt-6">
              <div
                className="rounded-lg px-4 py-2.5 text-center"
                style={{
                  border: '1.5px solid rgba(245,158,11,0.7)',
                  background: 'rgba(0,0,0,0.8)',
                  minWidth: 140,
                }}
              >
                <div className="text-xs font-mono mb-1" style={{ color: 'rgba(245,158,11,0.6)' }}>
                  Load Balancer
                </div>
                <div className="text-sm font-bold font-mono" style={{ color: '#f59e0b' }}>
                  ALB
                </div>
              </div>
            </div>

            {/* Row 3: EC2 ASG + RDS */}
            <div className="flex justify-center gap-8 mt-6">
              <div
                className="rounded-lg px-4 py-2.5 text-center"
                style={{
                  border: '1.5px solid rgba(245,158,11,0.7)',
                  background: 'rgba(0,0,0,0.8)',
                  minWidth: 120,
                }}
              >
                <div className="text-xs font-mono mb-1" style={{ color: 'rgba(245,158,11,0.6)' }}>
                  Compute
                </div>
                <div className="text-sm font-bold font-mono" style={{ color: '#f59e0b' }}>
                  EC2 ASG
                </div>
              </div>
              <div
                className="rounded-lg px-4 py-2.5 text-center"
                style={{
                  border: '1.5px solid rgba(245,158,11,0.7)',
                  background: 'rgba(0,0,0,0.8)',
                  minWidth: 130,
                }}
              >
                <div className="text-xs font-mono mb-1" style={{ color: 'rgba(245,158,11,0.6)' }}>
                  Database
                </div>
                <div className="text-sm font-bold font-mono" style={{ color: '#f59e0b' }}>
                  RDS Multi-AZ
                </div>
              </div>
            </div>

            {/* Row 4: S3, ElastiCache, SQS */}
            <div className="flex justify-center gap-4 mt-6 pb-10">
              <div
                className="rounded-md px-3 py-1.5 text-center"
                style={{
                  border: '1.5px solid rgba(245,158,11,0.5)',
                  background: 'rgba(0,0,0,0.8)',
                }}
              >
                <div className="text-xs font-bold font-mono" style={{ color: '#f59e0b' }}>
                  S3
                </div>
              </div>
              <div
                className="rounded-md px-3 py-1.5 text-center"
                style={{
                  border: '1.5px solid rgba(245,158,11,0.5)',
                  background: 'rgba(0,0,0,0.8)',
                }}
              >
                <div className="text-xs font-bold font-mono" style={{ color: '#f59e0b' }}>
                  ElastiCache
                </div>
              </div>
              <div
                className="rounded-md px-3 py-1.5 text-center"
                style={{
                  border: '1.5px solid rgba(245,158,11,0.5)',
                  background: 'rgba(0,0,0,0.8)',
                }}
              >
                <div className="text-xs font-bold font-mono" style={{ color: '#f59e0b' }}>
                  SQS
                </div>
              </div>
            </div>

            {/* Cost summary bottom-right */}
            <div
              className="absolute bottom-3 right-4 text-xs text-muted-foreground font-mono"
              style={{ color: 'rgba(245,158,11,0.7)' }}
            >
              $847/mo · 8 resources · v3
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 px-6 max-w-screen-2xl mx-auto scroll-mt-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-3">Everything your team needs</h2>
          <p className="text-muted-foreground">
            From first prompt to deployed infrastructure — one tool for the entire workflow.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f) => (
            <div
              key={f.id}
              className={`card-dark border ${f.bg} p-6 rounded-xl transition-all duration-200 hover:border-primary/30`}
            >
              <div
                className={`w-10 h-10 rounded-lg bg-card flex items-center justify-center mb-4 ${f.color}`}
              >
                <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={20} />
              </div>
              <h3 className="font-semibold text-base mb-2">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works.

          Matches Pricing's own shape below on purpose: a centred heading,
          then a plain three-column grid of individual cards, each one's
          content stacked top to bottom. Three cards side by side is what
          "space allows" actually means here -- the grid gives them that
          whenever there's `md` width to spare, and stacks them full-width,
          one per row, only once there genuinely isn't. */}
      <section id="how-it-works" className="py-20 px-6 max-w-screen-2xl mx-auto scroll-mt-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-3">From idea to infrastructure in 3 steps</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <div key={s.id} className="relative">
              {/* The connector reads left to right with the sequence, so it
                  only makes sense once the grid actually has three columns
                  to draw it across -- hidden below `md`, where the cards
                  stack instead. */}
              {i < STEPS.length - 1 && (
                <div
                  className="hidden md:block absolute top-6 left-full h-px bg-gradient-to-r from-primary/40 to-transparent z-10"
                  style={{ width: 'calc(100% - 1.5rem)' }}
                  aria-hidden
                />
              )}
              <div className="card-dark p-6 rounded-xl h-full">
                <div className="text-4xl font-bold text-gradient-blue font-tabular mb-4">
                  {s.num}
                </div>
                <h3 className="font-semibold text-lg mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials -- same shape as Steps and Pricing: a centred heading,
          a plain three-column grid, each card's own content (the quote, then
          the author) stacked vertically. */}
      <section className="py-20 px-6 max-w-screen-2xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-3">Trusted by cloud engineers</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t) => (
            <div key={t.id} className="card-dark p-6 rounded-xl h-full flex flex-col">
              <p className="text-sm text-muted-foreground leading-relaxed mb-4 italic flex-1">
                &ldquo;{t.text}&rdquo;
              </p>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center text-xs font-semibold text-primary flex-shrink-0">
                  {t.avatar}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{t.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-6 max-w-screen-2xl mx-auto scroll-mt-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-3">Simple, transparent pricing</h2>
          <p className="text-muted-foreground">Start free. Upgrade when you need more.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {PRICING.map((p) => (
            <div
              key={p.id}
              className={`card-dark p-6 rounded-xl relative ${p.highlight ? 'border-primary/60 glow-blue' : ''}`}
            >
              {p.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-2xs font-bold px-3 py-1 rounded-full bg-primary text-white">
                  Most Popular
                </div>
              )}
              <div className="mb-4">
                <div className="text-sm font-semibold text-muted-foreground mb-1">{p.name}</div>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold font-tabular">{p.price}</span>
                  <span className="text-muted-foreground text-sm">{p.period}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{p.desc}</p>
              </div>
              <ul className="space-y-2 mb-6">
                {p.features.map((f) => (
                  <li key={`${p.id}-feat-${f}`} className="flex items-center gap-2 text-sm">
                    <Icon name="CheckIcon" size={14} className="text-success flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                href="/login"
                className={`block text-center py-2 rounded-md text-sm font-semibold transition-all duration-150 ${p.highlight ? 'btn-primary' : 'btn-secondary'}`}
              >
                {p.cta}
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-10 px-6">
        <div className="max-w-screen-2xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <AppLogo size={24} />
            <span className="font-semibold">Archlyy</span>
            <span className="text-muted-foreground text-sm">
              — Multi-cloud infrastructure design
            </span>
          </div>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            {['Privacy', 'Terms', 'Docs', 'Status'].map((link) => (
              <button key={`footer-${link}`} className="hover:text-foreground transition-colors">
                {link}
              </button>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
